import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  agencyAPI, agentAPI, contractRequestAPI, userAPI, carrierAPI,
  contractTypeAPI, levelAPI, agencyEnablementAPI, agencyAppointmentAPI,
} from '@/lib/api';
import { isKRRole, isAccountingRole } from '@/lib/utils';
import { AgencyHeader } from '@/components/agency/AgencyHeader';
import { CRMNotesPanel } from '@/components/crm/CRMNotesPanel';
import { ActivityFeed } from '@/components/crm/ActivityFeed';
import PaymentInformationCard from '@/components/payment/PaymentInformationCard';
import PaymentHistoryCard from '@/components/payment/PaymentHistoryCard';
import PayeeEftCard from '@/components/payment/PayeeEftCard';
import RecipientAccountingSection from '@/components/accounting/RecipientAccountingSection';
import RecipientStatementsCard from '@/components/accounting/RecipientStatementsCard';
import {
  AgencySnapshot,
  AgencyAgentsSummary,
  AgencyContractingPipeline,
  AgencyCarrierPortfolio,
  AgencyDownlineTree,
  AgencyCompliance,
  AgencyTimeline,
  AgencyCommunicationSummary,
} from '@/components/agency/blocks';
import {
  EditAgencyDialog,
  EnableCarrierDialog,
  DisableCarrierDialog,
  AddAgencyAppointmentDialog,
  DeleteAgencyAppointmentDialog,
  UpdateAgencyInfoLinkDialog,
} from '@/components/agency/dialogs';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '@/components/ui/tabs';

/**
 * AgencyProfilePage — pure orchestrator for the Agency Command Center.
 *
 * Responsibilities (kept intentionally small):
 *   1. Fetch agency + related data (parent, children, agents, requests,
 *      principals, carriers, appointments, enablements, summary).
 *   2. Resolve permissions (isKR).
 *   3. Toggle dialog visibility and pass down refresh callbacks.
 *   4. Compose the header + dashboard blocks + dialogs.
 *
 * The page contains ONLY the agency workspace surface — Identity, Snapshot,
 * Downline, Agents, Contracting Pipeline, Carrier Portfolio, Compliance,
 * and Timeline.
 */
export default function AgencyProfilePage() {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isKR = isKRRole(user);
  const isAccounting = isAccountingRole(user);
  // Phase 27.19 — Bank/tax stays KR+Accounting only; but the accordion
  // section (with paid-statement history + ledger totals) is now also
  // visible to the agency principal/staff of this exact agency so they
  // can review their own payments. Walk user.roles[] since there's no
  // top-level user.agency_id scalar in this codebase.
  const AGENCY_OWNER_ROLES = new Set(['Agency_Principal', 'Agency_Staff', 'Agency_User', 'Manager']);
  const canSeeSensitivePaymentInfo = isKR || isAccounting;
  const isOwningAgency = (user?.roles || []).some(
    (r) => AGENCY_OWNER_ROLES.has(r?.role) && String(r?.agency_id) === String(agencyId),
  );
  const canSeePaymentInfo = canSeeSensitivePaymentInfo || isOwningAgency;

  const [loading, setLoading] = useState(true);
  const [agency, setAgency] = useState(null);
  const [parentAgency, setParentAgency] = useState(null);
  const [childAgencies, setChildAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [principalUsers, setPrincipalUsers] = useState([]);
  const [summary, setSummary] = useState(null);

  // Carrier catalog + enablement + appointments (KR-only for catalogs).
  const [allCarriers, setAllCarriers] = useState([]);
  const [allContractTypes, setAllContractTypes] = useState([]);
  const [allLevels, setAllLevels] = useState([]);
  const [enabledCarriers, setEnabledCarriers] = useState([]);
  const [agencyAppointments, setAgencyAppointments] = useState([]);
  // Phase 27.38 — Bump to force PaymentInformationCard to remount after
  // an EFT approval so its "Direct Deposit" status flips to Complete
  // without a page reload.
  const [paymentInfoRefreshKey, setPaymentInfoRefreshKey] = useState(0);

  // Dialog visibility flags.
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUpdateInfoDialog, setShowUpdateInfoDialog] = useState(false);
  const [showEnableCarrierDialog, setShowEnableCarrierDialog] = useState(false);
  const [showDisableCarrierDialog, setShowDisableCarrierDialog] = useState(false);
  const [showApptDialog, setShowApptDialog] = useState(false);
  const [showDeleteApptDialog, setShowDeleteApptDialog] = useState(false);

  // Selected records for dialogs (parent-owned; dialogs stay stateless
  // containers for the chosen entity).
  const [enablementToDelete, setEnablementToDelete] = useState(null);
  const [editingAppt, setEditingAppt] = useState(null);
  const [apptToDelete, setApptToDelete] = useState(null);

  // Phase 28 — Tab workspace mirroring the Agent Profile. Deep-link via
  // ?tab=… ; every module lives in exactly one scannable tab instead of
  // one long accordion scroll.
  const [searchParams] = useSearchParams();
  const deepLinkTab = searchParams.get('tab');
  const TAB_MAP = {
    overview: 'overview', snapshot: 'overview', timeline: 'overview',
    team: 'agents', agents: 'agents',
    downline: 'downline',
    contracting: 'contracting',
    carriers: 'carriers',
    payment: 'accounting', accounting: 'accounting',
    compliance: 'compliance',
    communication: 'communication', communications: 'communication',
  };
  const [activeTab, setActiveTab] = useState(() => TAB_MAP[deepLinkTab] || 'overview');
  useEffect(() => {
    if (deepLinkTab && TAB_MAP[deepLinkTab]) setActiveTab(TAB_MAP[deepLinkTab]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTab]);

  useEffect(() => { loadAgencyData(); }, [agencyId]);

  const loadAgencyData = async () => {
    try {
      const [agencyRes, allAgenciesRes, agentsRes, requestsRes] = await Promise.all([
        agencyAPI.get(agencyId),
        agencyAPI.list(),
        agentAPI.list({ agency_id: agencyId }),
        contractRequestAPI.list({ agency_id: agencyId }),
      ]);

      setAgency(agencyRes.data);
      const all = allAgenciesRes.data;
      setParentAgency(agencyRes.data.parent_agency_id
        ? all.find((a) => a.agency_id === agencyRes.data.parent_agency_id)
        : null);
      setChildAgencies(all.filter((a) => a.parent_agency_id === agencyId));
      setAgents(agentsRes.data.filter((a) => a.agency_id === agencyId));
      setRequests(requestsRes.data.filter((r) => r.requesting_agency_id === agencyId));

      // KR-only enrichments: principals, carrier catalogs, enablement, appts.
      if (isKR) {
        try {
          const [usersRes, carriersRes, typesRes, levelsRes, enabledRes, apptsRes] = await Promise.all([
            userAPI.list(),
            carrierAPI.list(),
            contractTypeAPI.list(),
            levelAPI.list(),
            agencyEnablementAPI.getEnabledCarriers(agencyId),
            agencyAppointmentAPI.list(agencyId),
          ]);
          setPrincipalUsers(
            usersRes.data.filter((u) =>
              u.roles?.some((r) => r.agency_id === agencyId && (r.role === 'Agency_Principal' || r.role === 'Agency_User')),
            ),
          );
          setAllCarriers(carriersRes.data.filter((c) => c.active));
          setAllContractTypes(typesRes.data.filter((t) => t.active));
          setAllLevels(levelsRes.data.filter((l) => l.active));
          setEnabledCarriers(enabledRes.data);
          setAgencyAppointments(apptsRes.data);
        } catch {
          // Non-fatal — non-KR viewers won't have permission for some catalogs.
        }
      }
    } catch {
      toast.error('Failed to load agency data');
      navigate('/agencies');
    } finally {
      setLoading(false);
    }
  };

  // Summary is a separate lightweight call so we can render blocks while
  // waiting for aggregate roll-ups.
  useEffect(() => {
    if (agencyId) {
      agencyAPI.getSummary(agencyId).then((res) => setSummary(res.data)).catch(() => {});
    }
  }, [agencyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!agency) return null;

  const canAddSub = isKR && agency.depth < 5;

  return (
    <div data-testid="agency-profile-page">
      <PageHeader
        title="Agency Profile"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Agencies', href: '/agencies' },
          { label: agency.agency_name, href: `/agencies/${agencyId}` },
        ]}
        actions={
          <Button variant="outline" onClick={() => navigate('/agencies')}>
            <ArrowLeft size={16} className="mr-2" />
            Back to Agency Tree
          </Button>
        }
      />

      <div className="page-content">
        {/* ═══════════ AGENCY OVERVIEW HEADER ═══════════ */}
        <AgencyHeader
          agency={agency}
          parentAgency={parentAgency}
          principalUsers={principalUsers}
          isKR={isKR}
          onEdit={() => setShowEditDialog(true)}
          onLogoChanged={loadAgencyData}
          onAddSubAgency={() => navigate(`/agencies?add_parent=${agencyId}`)}
          onUpdateInfo={() => setShowUpdateInfoDialog(true)}
        />

        {/* ═══════════ TAB WORKSPACE (Phase 28) ═══════════
            Reorganized to mirror the Agent Profile: every block is
            preserved, grouped into scannable tabs instead of one long
            accordion. Each piece of data lives in exactly one tab. */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="sticky top-0 z-20 bg-slate-50/95 backdrop-blur-sm py-1.5 mb-4 border-b border-slate-200">
            <TabsList className="w-full justify-start flex-nowrap overflow-x-auto h-auto" data-testid="agency-profile-tabs">
              <TabsTrigger value="overview" data-testid="agency-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="agents" data-testid="agency-tab-agents">Agents</TabsTrigger>
              <TabsTrigger value="downline" data-testid="agency-tab-downline">Downline</TabsTrigger>
              <TabsTrigger value="contracting" data-testid="agency-tab-contracting">Contracting</TabsTrigger>
              <TabsTrigger value="carriers" data-testid="agency-tab-carriers">Carriers</TabsTrigger>
              {canSeePaymentInfo && (
                <TabsTrigger value="accounting" data-testid="agency-tab-accounting">Accounting</TabsTrigger>
              )}
              <TabsTrigger value="compliance" data-testid="agency-tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="communication" data-testid="agency-tab-communication">Communication</TabsTrigger>
            </TabsList>
          </div>

          {/* ── OVERVIEW ── snapshot + notes/activity + timeline ── */}
          <TabsContent value="overview" className="space-y-5 mt-0" data-testid="agency-overview-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-snapshot">
              <h3 className="text-base font-heading font-semibold mb-3">Snapshot</h3>
              <AgencySnapshot
                agency={agency}
                summary={summary}
                appointments={agencyAppointments}
                enabledCarriers={enabledCarriers}
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <div className="border rounded-lg bg-white p-4" data-testid="agency-section-notes">
                <h3 className="text-base font-heading font-semibold mb-3">Notes</h3>
                <CRMNotesPanel subjectType="agency" subjectId={agencyId} subjectAgencyId={agencyId} />
              </div>
              <div className="border rounded-lg bg-white p-4" data-testid="agency-section-activity">
                <h3 className="text-base font-heading font-semibold mb-3">Activity</h3>
                <ActivityFeed subjectType="agency" subjectId={agencyId} />
              </div>
            </div>
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-timeline">
              <h3 className="text-base font-heading font-semibold mb-3">Timeline</h3>
              <AgencyTimeline agencyId={agencyId} />
            </div>
          </TabsContent>

          {/* ── AGENTS ── */}
          <TabsContent value="agents" className="mt-0" data-testid="agency-agents-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-team">
              <AgencyAgentsSummary agents={agents} agencyId={agencyId} />
            </div>
          </TabsContent>

          {/* ── DOWNLINE ── */}
          <TabsContent value="downline" className="mt-0" data-testid="agency-downline-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-downline">
              <AgencyDownlineTree
                childAgencies={childAgencies}
                canAddChild={canAddSub}
                agencyId={agencyId}
              />
            </div>
          </TabsContent>

          {/* ── CONTRACTING ── */}
          <TabsContent value="contracting" className="mt-0" data-testid="agency-contracting-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-contracting">
              <AgencyContractingPipeline
                requests={requests}
                appointments={agencyAppointments}
                agencyId={agencyId}
              />
            </div>
          </TabsContent>

          {/* ── CARRIERS ── */}
          <TabsContent value="carriers" className="mt-0" data-testid="agency-carriers-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-carriers">
              <AgencyCarrierPortfolio
                appointments={agencyAppointments}
                enabledCarriers={enabledCarriers}
                isKR={isKR}
                onAddAppointment={() => { setEditingAppt(null); setShowApptDialog(true); }}
                onEditAppointment={(row) => { setEditingAppt(row); setShowApptDialog(true); }}
                onDeleteAppointment={(row) => { setApptToDelete(row); setShowDeleteApptDialog(true); }}
                onEnableCarrier={() => setShowEnableCarrierDialog(true)}
                onDisableCarrier={(item) => { setEnablementToDelete(item); setShowDisableCarrierDialog(true); }}
              />
            </div>
          </TabsContent>

          {/* ── ACCOUNTING ── payment info + statements ──
              Bank/tax stays KR+Accounting only via canSeeSensitivePaymentInfo;
              statements show the recipient's own payout figures (never the
              confidential KR-received amount). */}
          {canSeePaymentInfo && agency && (
            <TabsContent value="accounting" className="mt-0 space-y-4" data-testid="agency-accounting-tab">
              {canSeeSensitivePaymentInfo && (
                <PaymentInformationCard
                  key={`pi-${paymentInfoRefreshKey}`}
                  recipientType={agency.parent_agency_id && (agency.depth || 0) > 1 ? 'subagency' : 'agency'}
                  recipientId={agency.agency_id}
                />
              )}
              <PayeeEftCard
                payeeType="Agency"
                payeeId={agency.agency_id}
                recipientType={agency.parent_agency_id && (agency.depth || 0) > 1 ? 'subagency' : 'agency'}
                recipientId={agency.agency_id}
                canView={canSeePaymentInfo}
                canManage={canSeeSensitivePaymentInfo}
                onDirectDepositSync={() => setPaymentInfoRefreshKey((k) => k + 1)}
              />
              <PaymentHistoryCard
                recipientType={agency.parent_agency_id && (agency.depth || 0) > 1 ? 'subagency' : 'agency'}
                recipientId={agency.agency_id}
              />
              <RecipientStatementsCard
                recipientType={agency.parent_agency_id && (agency.depth || 0) > 1 ? 'subagency' : 'agency'}
                recipientId={agency.agency_id}
              />
              <RecipientAccountingSection
                recipientType={agency.parent_agency_id && (agency.depth || 0) > 1 ? 'subagency' : 'agency'}
                recipientId={agency.agency_id}
              />
            </TabsContent>
          )}

          {/* ── COMPLIANCE ── */}
          <TabsContent value="compliance" className="mt-0" data-testid="agency-compliance-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-compliance">
              <AgencyCompliance agents={agents} appointments={agencyAppointments} />
            </div>
          </TabsContent>

          {/* ── COMMUNICATION ── */}
          <TabsContent value="communication" className="mt-0" data-testid="agency-communication-tab">
            <div className="border rounded-lg bg-white p-4" data-testid="agency-section-communication">
              <AgencyCommunicationSummary agencyId={agencyId} />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ═══════════ DIALOGS ═══════════ */}
      <EditAgencyDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        agency={agency}
        onSaved={loadAgencyData}
      />

      <UpdateAgencyInfoLinkDialog
        open={showUpdateInfoDialog}
        onOpenChange={setShowUpdateInfoDialog}
        agency={agency}
      />

      <EnableCarrierDialog
        open={showEnableCarrierDialog}
        onOpenChange={setShowEnableCarrierDialog}
        agencyId={agencyId}
        allCarriers={allCarriers}
        allContractTypes={allContractTypes}
        allLevels={allLevels}
        onSaved={loadAgencyData}
      />

      <DisableCarrierDialog
        open={showDisableCarrierDialog}
        onOpenChange={setShowDisableCarrierDialog}
        enablement={enablementToDelete}
        onRemoved={() => { setEnablementToDelete(null); loadAgencyData(); }}
      />

      <AddAgencyAppointmentDialog
        open={showApptDialog}
        onOpenChange={setShowApptDialog}
        agencyId={agencyId}
        editing={editingAppt}
        allCarriers={allCarriers}
        allContractTypes={allContractTypes}
        allLevels={allLevels}
        onSaved={loadAgencyData}
      />

      <DeleteAgencyAppointmentDialog
        open={showDeleteApptDialog}
        onOpenChange={setShowDeleteApptDialog}
        agencyId={agencyId}
        appointment={apptToDelete}
        onDeleted={() => { setApptToDelete(null); loadAgencyData(); }}
      />
    </div>
  );
}
